import React, { forwardRef, useImperativeHandle, useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, Grid } from '@material-ui/core'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import FadeIn from '../../../../molecules/Transitions/FadeIn'
import SydLabel, { hookFormErrorAdapter } from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import SydSelect from '../../../../commonDesign/SydSelect'
import { fsExcludeClass } from '../../../../../utils/fsExclude'
import { useAbundanceEngineBranding } from '../../../../../api/abundanceEngine'
import Loader from '../../Models/Loader'
import { useFeatureFlag } from '../../../../../redux/slices/appConfig'
import { FEATURE_FLAG } from '../../../../../constants'

const useStyles = makeStyles((theme) => ({
  userInfoForm: {
    maxWidth: '800px',
    display: 'flex',
    justifyContent: 'center',
    padding: '10px',
    '& p': {
      color: 'var(--color-primary-600)'
    }
  }
}))

const useFormValues = (user) => {
  const defaultValues = useMemo(() => {
    return {
      userId: user.userId,
      externalUserId: user.externalUserId,
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      brandingConfigKey: user?.brandingConfigKey || ''
    }
  }, [user])

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues
  })

  return formMethods
}

const AdvisorUserInfoForm = forwardRef(function AdvisorUserInfoForm ({ user }, ref) {
  const classes = useStyles()
  const form = useFormValues(user)
  const { data: brandingOptions, isLoading: isLoadingBranding } = useAbundanceEngineBranding()
  const { handleSubmit, formState, ...formValues } = form

  const brandingSelectOptions = useMemo(() => {
    if (!brandingOptions) return []
    return brandingOptions.map(option => ({
      value: option.brandKey,
      label: option.brandName || option.brandKey
    }))
  }, [brandingOptions])

  const { active: multiBrand } = useFeatureFlag(FEATURE_FLAG.MULTI_BRAND_FIRM)

  useImperativeHandle(ref, () => ({
    handleSubmit
  }), [handleSubmit])

  if (isLoadingBranding && multiBrand) {
    return <Loader />
  }
  return (
    <FormProvider handleSubmit={handleSubmit} formState={formState} {...formValues}>
      <FadeIn className={classes.userInfoForm}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <Controller
              name='firstName'
              rules={{ required: { value: true, message: 'Required' }, maxLength: { value: 30, message: 'Too long' } }}
              render={({ field, fieldState }) => (
                <SydLabel
                  label='First Name'
                  required description='Required, 30 max characters'
                  error={hookFormErrorAdapter(form, fieldState)}
                >
                  <SydInput className={fsExcludeClass} size='sm' placeholder='First Name' {...field} />
                </SydLabel>
              )}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <Controller
              name='lastName'
              rules={{ required: { value: true, message: 'Required' }, maxLength: { value: 30, message: 'Too long' } }}
              render={({ field, fieldState }) => (
                <SydLabel
                  label='Last Name'
                  required description='Required, 30 max characters'
                  error={hookFormErrorAdapter(form, fieldState)}
                >
                  <SydInput className={fsExcludeClass} size='sm' placeholder='Last Name' {...field} />
                </SydLabel>
              )}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <SydLabel
              label='Email'
            >
              <SydInput className={fsExcludeClass} size='sm' value={user.email} disabled />
            </SydLabel>
          </Grid>
          <Grid item xs={12} md={12}>
            <SydLabel
              label='External User Id'
            >
              <SydInput size='sm' value={user.externalUserId} disabled />
            </SydLabel>
          </Grid>

          {multiBrand ? (
            <Grid item xs={12} md={12}>
              <Controller
                name='brandingConfigKey'
                rules={{ required: { value: true, message: 'Required' } }}
                render={({ field, fieldState }) => (
                  <SydLabel
                    label='Branding'
                    required description='Required'
                    error={hookFormErrorAdapter(form, fieldState)}
                  >
                    <SydSelect
                      {...field}
                      options={brandingSelectOptions}
                      placeholder='Select Branding:'
                      size='lg'
                    />
                  </SydLabel>
                )}
              />
            </Grid>
          ) : null}
        </Grid>
      </FadeIn>
    </FormProvider>
  )
})

AdvisorUserInfoForm.propTypes = {
  user: PropTypes.shape({
    userId: PropTypes.number,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    externalUserId: PropTypes.string,
    brandingConfigKey: PropTypes.string
  })
}

export default AdvisorUserInfoForm
